import { useState } from 'react';

interface DealerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DealerModal({ isOpen, onClose }: DealerModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => body.append(key, value as string));
    try {
      await fetch('https://readdy.ai/api/form/d72m544ij602egd95l10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#151515] border border-amber-500/20 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
          <div>
            <h3 className="text-white font-black text-xl">代理店・導入相談</h3>
            <p className="text-gray-400 text-sm mt-0.5">フィジオショット導入のご相談フォーム</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white cursor-pointer">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {!submitted ? (
          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="px-6 py-6 space-y-4"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-gray-300 text-sm font-medium block mb-1.5">施設名 <span className="text-amber-400">*</span></label>
                <input
                  type="text"
                  name="facility_name"
                  required
                  placeholder="〇〇整骨院"
                  className="w-full bg-[#1A1A1A] border border-white/10 rounded-md px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-amber-500/50"
                />
              </div>
              <div>
                <label className="text-gray-300 text-sm font-medium block mb-1.5">担当者名 <span className="text-amber-400">*</span></label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="山田 太郎"
                  className="w-full bg-[#1A1A1A] border border-white/10 rounded-md px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-amber-500/50"
                />
              </div>
            </div>

            <div>
              <label className="text-gray-300 text-sm font-medium block mb-1.5">メールアドレス <span className="text-amber-400">*</span></label>
              <input
                type="email"
                name="email"
                required
                placeholder="your@email.com"
                className="w-full bg-[#1A1A1A] border border-white/10 rounded-md px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-amber-500/50"
              />
            </div>

            <div>
              <label className="text-gray-300 text-sm font-medium block mb-1.5">電話番号 <span className="text-amber-400">*</span></label>
              <input
                type="tel"
                name="phone"
                required
                placeholder="090-0000-0000"
                className="w-full bg-[#1A1A1A] border border-white/10 rounded-md px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-amber-500/50"
              />
            </div>

            <div>
              <label className="text-gray-300 text-sm font-medium block mb-1.5">施設種別 <span className="text-amber-400">*</span></label>
              <select
                name="facility_type"
                required
                className="w-full bg-[#1A1A1A] border border-white/10 rounded-md px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/50"
              >
                <option value="">選択してください</option>
                <option value="整体院">整体院</option>
                <option value="整骨院">整骨院</option>
                <option value="接骨院">接骨院</option>
                <option value="ジム・フィットネス">ジム・フィットネス</option>
                <option value="スポーツジム">スポーツジム</option>
                <option value="病院・クリニック">病院・クリニック</option>
                <option value="その他">その他</option>
              </select>
            </div>

            <div>
              <label className="text-gray-300 text-sm font-medium block mb-1.5">現在の月商規模</label>
              <select
                name="monthly_revenue"
                className="w-full bg-[#1A1A1A] border border-white/10 rounded-md px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/50"
              >
                <option value="">選択してください（任意）</option>
                <option value="100万円未満">100万円未満</option>
                <option value="100〜300万円">100〜300万円</option>
                <option value="300〜500万円">300〜500万円</option>
                <option value="500万円以上">500万円以上</option>
              </select>
            </div>

            <div>
              <label className="text-gray-300 text-sm font-medium block mb-1.5">導入に関するご相談・ご質問</label>
              <textarea
                name="message"
                rows={3}
                maxLength={500}
                placeholder="導入のきっかけや、特に知りたいことをご記入ください（500文字以内）"
                className="w-full bg-[#1A1A1A] border border-white/10 rounded-md px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-amber-500/50 resize-none"
                onChange={(e) => setCharCount(e.target.value.length)}
              />
              <p className="text-gray-600 text-xs text-right mt-1">{charCount}/500</p>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-black text-base rounded-md transition-all cursor-pointer whitespace-nowrap"
            >
              {submitting ? '送信中...' : '無料で導入相談を申し込む'}
            </button>
            <p className="text-gray-500 text-xs text-center">通常24時間以内にご連絡します。まずはお気軽にご相談ください。</p>
          </form>
        ) : (
          <div className="px-6 py-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-amber-500/10 rounded-full mx-auto mb-4">
              <i className="ri-check-line text-amber-400 text-3xl" />
            </div>
            <h4 className="text-white font-black text-xl mb-2">お問い合わせが完了しました！</h4>
            <p className="text-gray-400 text-sm leading-relaxed">
              担当者より通常24時間以内にご連絡いたします。<br />
              導入コストや収益シミュレーションをご提示します。
            </p>
            <button onClick={onClose} className="mt-6 px-8 py-3 bg-amber-500 text-black font-bold rounded-md cursor-pointer whitespace-nowrap">
              閉じる
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
